import { PlannerConfig, FeatureConfig, ConfigOptions } from '../@types';
import { plannerComponentResolver } from '@inter-ikea-storageone/range';
import { paxComponentResolver } from '@inter-ikea-storageone/range-pax';
import proppingCatalog from '@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json';
import proppingTemplate from '@inter-ikea-storageone/range-bedroom-storage-propping/data/template.json';
import paxInteriorSuggestionsCatalog from '@inter-ikea-storageone/range-pax/data/interior-suggestions/catalog.json';
import paxInteriorSuggestionsTemplate from '@inter-ikea-storageone/range-pax/data/interior-suggestions/template.json';
import paxCatalog from '@inter-ikea-storageone/range-pax/data/catalog.json';
import paxTemplate from '@inter-ikea-storageone/range-pax/data/template.json';
import handlesCatalog from '@inter-ikea-storageone/range-handles/data/catalog.json';
import handlesTemplate from '@inter-ikea-storageone/range-handles/data/template.json';
import PaxCameraBehavior from 'rangeConfig/pax/camera/PaxCameraBehavior';
import frames from './productGroups/frames';
import doors from './productGroups/doors';
import handles from './productGroups/handles';
import interiors from './productGroups/interiors';
import accessories from './productGroups/accessories';
import lights from './productGroups/lights';
import { CustomizedSlidingDoor } from 'rangeConfig/pax/features/CustomizedSlidingDoor';
import { LightsSelection } from 'rangeConfig/shared/features/LightsSelection';
import { HideDrawerFront } from 'rangeConfig/pax/features/HideDrawerFront';
import { TooManyLightsFeature } from 'rangeConfig/shared/features/TooManyLights/TooManyLightsFeature';
import { RecommendHandles } from 'rangeConfig/shared/features/RecommendHandles/RecommendHandles';
import { ToggleDoorsFeature as ToggleDoors } from 'rangeConfig/shared/features/ToggleHingeDoors';
import { DoorOpeningDirection } from 'rangeConfig/pax/features/DoorOpeningDirection';
import platform from 'rangeConfig/shared/utils/platform';
import { getOptimizedModels } from 'rangeConfig/shared/utils/optimizedModels';
import MeasureAction from 'components/SceneMenu/actions/MeasureAction';
import DoorsAction from 'components/SceneMenu/actions/DoorsAction';
import ProppingAction from 'components/SceneMenu/actions/ProppingAction';
import { HandlesDrillTemplate } from 'rangeConfig/shared/features/HandlesDrillTemplate/HandlesDrillTemplate';
import { CoverCapsFeature } from './features/CoverCaps/CoverCapsFeature';
import { getQuery } from 'utils/queryUtils';
import { MultiWallSeeThroughFeature } from 'rangeConfig/shared/features/MultiWallSeeThroughFeature';
import { NightLightsFeature } from 'rangeConfig/shared/features/NightLights/NightLightsFeature';
import { ClothHangersFeature } from 'rangeConfig/shared/features/ClothHangers/ClothHangersFeature';
const defaultAssetsPath = '/assets/';
const assetsPath = import.meta.env.REACT_APP_ASSETS_PATH || defaultAssetsPath;
const screensaverPath = `${assetsPath}screensaver/`;
const mainScreensaverPath = `${screensaverPath}screensaver_PAX_hero_master_no_logo.mp4`;
const secondaryScreensaverPath = `${screensaverPath}screensaver_PAX_hero_us_no_logo.mp4`;
import sprToVpcMap from '@inter-ikea-storageone/range-pax/data/spr2vpc.json';
import DiscoAction from 'components/SceneMenu/actions/DiscoAction';
import paxLandingPageConfig from './paxLandingPageConfig';
import { AutofillPax } from 'rangeConfig/pax/features/AutofillPax/AutofillPax';
import { DistanceMeasurementFeature } from 'rangeConfig/shared/features/DistanceMeasurementFeature';

const paxRange = (options?: ConfigOptions): PlannerConfig => {
  const features: { mobile: FeatureConfig[]; desktop: FeatureConfig[] } = {
    mobile: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'CustomizedSlidingDoor', feature: CustomizedSlidingDoor },
      { id: 'RecommendHandles', feature: RecommendHandles },
      { id: 'ToggleDoors', feature: ToggleDoors },
      { id: 'DoorOpeningDirection', feature: DoorOpeningDirection },
      { id: 'HideDrawerFront', feature: HideDrawerFront },
      { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
      { id: 'CoverCaps', feature: CoverCapsFeature },
      { id: 'TooManyLights', feature: TooManyLightsFeature },
      { id: 'AutofillPax', feature: AutofillPax }
    ],
    desktop: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'DistanceMeasurement', feature: DistanceMeasurementFeature },
      { id: 'CustomizedSlidingDoor', feature: CustomizedSlidingDoor },
      { id: 'LightsSelection', feature: LightsSelection },
      { id: 'HideDrawerFront', feature: HideDrawerFront },
      { id: 'RecommendHandles', feature: RecommendHandles },
      { id: 'ToggleDoors', feature: ToggleDoors },
      { id: 'DoorOpeningDirection', feature: DoorOpeningDirection },
      { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
      { id: 'CoverCaps', feature: CoverCapsFeature },
      { id: 'NightLights', feature: NightLightsFeature },
      { id: 'TooManyLights', feature: TooManyLightsFeature },
      { id: 'AutofillPax', feature: AutofillPax }
    ]
  };

  if (options?.featureFlags?.bumerangHangers) {
    features.mobile.push({ id: 'ClothHangers', feature: ClothHangersFeature });
    features.desktop.push({ id: 'ClothHangers', feature: ClothHangersFeature });
  }

  const productGroups = (options?: ConfigOptions) => {
    const productGroups = {
      desktop: [frames, doors, handles, interiors, accessories, lights],
      mobile: [frames, doors, handles, interiors, lights]
    };

    return productGroups[platform].map(group => group(options));
  };

  const sceneActions = {
    desktop: [MeasureAction, DoorsAction, ProppingAction],
    mobile: [DoorsAction, MeasureAction]
  };

  const discoMode = getQuery().get('enableDiscoMode') === 'true';
  if (discoMode) {
    sceneActions.desktop.push(DiscoAction);
  }

  return {
    componentResolvers: [plannerComponentResolver, paxComponentResolver],
    productCatalogs: [
      {
        name: 'pax',
        catalog: getOptimizedModels(paxCatalog),
        template: paxTemplate,
        assetsPath
      },
      {
        name: 'pax-interior-suggestions',
        catalog: paxInteriorSuggestionsCatalog,
        template: paxInteriorSuggestionsTemplate,
        assetsPath
      },
      {
        name: 'handles',
        catalog: getOptimizedModels(handlesCatalog),
        template: handlesTemplate,
        assetsPath
      },
      {
        name: 'propping',
        catalog: getOptimizedModels(proppingCatalog),
        template: proppingTemplate,
        assetsPath
      }
    ],
    sprToVpcMap,
    screensaverPath: mainScreensaverPath,
    secondaryScreensaverPath: secondaryScreensaverPath,
    cameraBehavior: PaxCameraBehavior,
    productGroups: productGroups(options),
    sceneActions: sceneActions[platform],
    features: features[platform],
    landingPageConfig: paxLandingPageConfig,
    featureToggles: []
  };
};

export default paxRange;
